import { Skeleton } from "@/components/ui/skeleton";
import LeadDrawer from "@/components/LeadDrawer";

const STAGES = ["New", "Contacted", "Qualified", "Meeting", "Proposal", "Won", "Lost"];

const stageAccent = {
  New: "border-slate-200",
  Contacted: "border-blue-200",
  Qualified: "border-amber-200",
  Meeting: "border-[#C5A880]",
  Proposal: "border-purple-200",
  Won: "border-emerald-300",
  Lost: "border-red-200",
};

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch (_e) {
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const lead = leads.find((l) => l.lead_id === id);
    if (!lead || lead.stage === stage) return;
    try {
      await api.patch(`/leads/${id}`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch (_e) { toast.error("Failed to move"); }
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8">
        <div className="label-eyebrow">Pipeline</div>
        <h1 className="font-serif-display text-4xl text-[#0A1128] mt-2 tracking-tight">Deal flow</h1>
        <p className="text-slate-500 mt-2 text-sm">Drag cards between columns to advance the stage.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
        {STAGES.map((stage) => {
          const cards = leads.filter((l) => l.stage === stage);
          return (
            <div
              key={stage}
              data-testid={`kanban-col-${stage}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, stage)}
              className={`stage-column bg-white border border-[#E2E8F0] rounded-lg p-3 min-h-[60vh] ${dragOver === stage ? "drag-over" : ""}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-600">{stage}</div>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{cards.length}</span>
              </div>
              <div className="space-y-2">
                {loading ? (
                  [...Array(2)].map((_, i) => (
                    <div key={i} className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-md p-3 space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-28" />
                      <Skeleton className="h-3 w-16 rounded-full" />
                    </div>
                  ))
                ) : (
                  cards.map((l) => (
                    <div
                      key={l.lead_id}
                      data-testid={`kanban-card-${l.lead_id}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, l.lead_id)}
                      onClick={() => setSelected(l)}
                      className={`kanban-card bg-white border-l-2 ${stageAccent[stage]} border border-[#E2E8F0] rounded-md p-3`}
                    >
                      <div className="text-sm font-medium text-[#0A1128] truncate">{l.name}</div>
                      <div className="text-xs text-slate-500 truncate">{l.company || l.email}</div>
                      <div className="text-[10px] tracking-widest uppercase text-[#C5A880] mt-2">{l.segment}</div>
                      {l.budget_sgd ? (
                        <div className="text-xs text-slate-700 mt-1">S${new Intl.NumberFormat().format(l.budget_sgd)}</div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <LeadDrawer lead={selected} onClose={() => setSelected(null)} onChange={load} />
    </div>
  );
}
